/* eslint-disable complexity */
import logger from '@scale/frontend-lib/logging';
import { InteractionInput } from '../../InteractionInput';
import { StepCard } from '../../step/StepCard';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import {
  DEFAULT_PROMPT_INPUT_SUBTITLE,
  DEFAULT_PROMPT_INPUT_TITLE,
  InteractionStepType,
  MessageRole,
  ReferenceText,
  Attachment,
  PromptInputStepResponse,
  ModelInputFileAttachmentType,
  AutoFeedbackChatResponse,
} from '@scale/shared/types/chat';
import { EditableChatMessage } from '../../EditableChatMessage';
import { useState, useEffect, useContext, memo, useMemo } from 'react';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { extractPrefilledMessages } from '@scale/shared/chat/response/validation';
import {
  useChatStoreExtract,
  useIsRealCustomerAudit,
  usePromptInputDisplayType,
  useStepParams,
  useStepResponse,
} from '../../../Chat/hooks/ChatStore.hooks';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import { StepWidthWrapper } from '../../step/StepWidthWrapper';
import ContributorFeedbackBox from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback';
import apiFetch from 'react-scale/lib/api-fetch';
import { StepViewComponentProps } from '../stepDescriptor/types';
import useEvaluatePromptText, { evaluatePromptText } from '../../hooks/useEvaluatePromptText';
import ModelPromptSelector from '../../ModelPromptSelector';
import AudioPromptInputTaskComponent from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/PromptInput/AudioPromptInputTaskComponent';
import { useShowContributorInstructionsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useShowContributorInstructionsStore';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
const PromptInputTaskComponent = ({
  readOnly,
  complete,
  responseIndex,
  collapsibleInstruction,
}: StepViewComponentProps) => {
  const taskContext = useContext(ChatTaskContext);

  const config = useChatStoreExtract(state => state.config);
  const step = useStepParams<InteractionStepType.PromptInput>(responseIndex);
  const response = useStepResponse<PromptInputStepResponse>(responseIndex);
  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });
  const { instructions } = step.params;
  const [expandInstructions] = useState<boolean>(false);
  const [selectedPrompt, setSelectedPrompt] = useState<string>('');
  const [displaySuggestionText, setDisplaySuggestionText] = useState<boolean>(
    !!step.params.generatePrompts,
  );
  const isRealCustomerAudit = useIsRealCustomerAudit();
  const customerAuditShowInstructions = useShowContributorInstructionsStore(
    state => state.customerAuditShowInstructions,
  );

  const shouldAlwaysShowContributorInstructions = useUnleashFeatureFlag(
    'audit-always-show-contributor-instructions',
    { projectId: taskContext.projectId },
    false,
  );

  const { taskingUserMessages: unevaluatedTaskingUserMessages } = useMemo(() => {
    return extractPrefilledMessages(config.initialMessages);
  }, [config.initialMessages]);

  const initialReferenceTexts = useMemo(() => {
    if (
      unevaluatedTaskingUserMessages.length > 0 &&
      unevaluatedTaskingUserMessages[0].referenceText
    ) {
      const { referenceText, referenceUrl } = unevaluatedTaskingUserMessages[0];
      return [
        {
          content: referenceText,
          url: referenceUrl,
        },
      ];
    }

    return [];
  }, [unevaluatedTaskingUserMessages]);

  const initialAttachments = useMemo(() => {
    if (
      unevaluatedTaskingUserMessages.length > 0 &&
      unevaluatedTaskingUserMessages[0].promptAttachments
    ) {
      // Filter out any attachments with empty / null urls
      // This happens if the URL's template variable is not set
      return unevaluatedTaskingUserMessages[0].promptAttachments.filter(
        attachment => attachment.url,
      );
    }

    // If preloaded images is enabled and the image url template variable is set, add it as an attachment
    if (
      config.imageProvidedByCustomer &&
      config.imageUrlTemplateVariable &&
      config.templateVariables?.[config.imageUrlTemplateVariable]
    ) {
      return [
        {
          name: config.templateVariables[config.imageUrlTemplateVariable],
          mimeType: 'unknown',
          fileType: ModelInputFileAttachmentType.Image,
          fileSizeInBytes: -1,
          url: config.templateVariables[config.imageUrlTemplateVariable],
        },
      ];
    }

    return [];
  }, [
    unevaluatedTaskingUserMessages,
    config.imageProvidedByCustomer,
    config.imageUrlTemplateVariable,
    config.templateVariables,
  ]);

  const [referenceTexts, setReferenceTexts] = useState<ReferenceText[]>(
    response?.context?.referenceTexts || initialReferenceTexts,
  );

  const responses = useChatStoreExtract(state => state.responses);

  const [attachments, setAttachments] = useState<Attachment[]>(
    response?.context?.attachments || initialAttachments,
  );
  const autoCritiqueResponse = response?.context?.autoCritiqueResponse || '';
  const { evaluatedPromptText, isEvaluatingPrompt } = useEvaluatePromptText(
    step.params.initial_prompt_text,
    responses,
    step,
  );

  // add image hashes to attachments asynchronously
  useEffect(() => {
    const getHashesForAttachments = async (attachments: Attachment[]) => {
      attachments.forEach((attachment: Attachment, idx: number) => {
        if (
          attachment.fileType === ModelInputFileAttachmentType.Image &&
          attachment.url &&
          !attachment.imageHashes
        ) {
          // if the api fetch for an attachment fails, we just ignore it
          apiFetch(null, 'internal/genai/imageHashesFromUrl', {
            method: 'POST',
            body: { url: attachment.url },
          })
            .then(resp => {
              const { hashes: imageHashes } = resp;
              attachments[idx] = { ...attachment, imageHashes };
              setAttachments([...attachments]);
              return;
            })
            .catch(() => {});
        }
      });
    };
    getHashesForAttachments(attachments);
  }, [attachments]);

  useEffect(() => {
    const fetchAndSetImageMetadata = async (attachments: Attachment[]) => {
      for (let idx = 0; idx < attachments.length; idx++) {
        const attachment = attachments[idx];

        if (!attachment.url || attachment.mimeType !== 'unknown') {
          continue;
        }

        try {
          // Use HEAD request to get metadata without downloading full image
          const response = await fetch(attachment.url, { method: 'HEAD' });
          if (!response.ok) {
            logger.error('Error getting image metadata for unknown mime type attachment', {
              status: response.status,
              url: attachment.url,
            });
            continue;
          }

          const mimeType = response.headers.get('content-type') || 'unknown';
          const contentLength = response.headers.get('content-length');
          const fileSizeInBytes = contentLength ? parseInt(contentLength, 10) : -1;

          attachments[idx] = {
            ...attachment,
            mimeType,
            fileSizeInBytes,
          };
          setAttachments([...attachments]);
        } catch (error) {
          logger.error('Error getting image metadata for unknown mime type attachment', {
            error,
            url: attachment.url,
          });
        }
      }
    };
    fetchAndSetImageMetadata(attachments);
  }, [attachments]);

  useEffect(() => {
    const setInitialMessages = async () => {
      const taskingUserMessages = await Promise.all(
        unevaluatedTaskingUserMessages.map(async message => ({
          ...message,
          content: await evaluatePromptText(message.content, responses, step),
        })),
      );

      if (
        taskingUserMessages.length > 0 &&
        !complete &&
        !response &&
        taskingUserMessages[0].content &&
        taskingUserMessages[0].content!.length > 0
      ) {
        const context = { referenceTexts, attachments };

        onSubmit(
          {
            type: InteractionStepType.PromptInput,
            context,
            output: taskingUserMessages[0].content!,
          },
          responseIndex,
          step,
          undefined,
          true /* skip linting */,
        );
      }
    };
    setInitialMessages();
  }, [
    config.initialMessages,
    complete,
    response,
    responseIndex,
    step,
    onSubmit,
    unevaluatedTaskingUserMessages,
    responses,
    referenceTexts,
    attachments,
  ]);

  const getModelPromptSelector = () => {
    if (config.isPromptGenerationEnabled) {
      return (
        <ModelPromptSelector
          isFirstTurn={true}
          responseIndex={responseIndex}
          projectId={taskContext.projectId}
          chatBenchmarkConfig={taskContext.chatBenchmarkConfig}
          complete={complete}
          readOnly={readOnly}
          referenceTexts={referenceTexts}
          attachments={attachments}
          setSelectedPrompt={setSelectedPrompt}
          setDisplaySuggestionText={setDisplaySuggestionText}
        />
      );
    }
    return null;
  };

  const promptInputDisplayType = usePromptInputDisplayType();
  if (isEvaluatingPrompt) {
    return null;
  }

  /** Support audio as a prompt input - this is gated behind a "enable audio recording" param in the model input step config */
  if (step.params.enableAudioRecording) {
    return (
      <AudioPromptInputTaskComponent
        step={step}
        response={response}
        attachments={attachments}
        setAttachments={setAttachments}
        readOnly={readOnly ?? false}
        onSubmit={onSubmit}
        responseIndex={responseIndex}
        collapsibleInstruction={collapsibleInstruction}
        complete={complete}
      />
    );
  }

  return (
    <>
      {(!isRealCustomerAudit ||
        customerAuditShowInstructions ||
        shouldAlwaysShowContributorInstructions) && (
        <StepWidthWrapper>
          <StepCard readOnly={readOnly}>
            <StepCard.Header>
              <StepCard.HeaderLeft>
                <StepCard.Icon icon="comment" />
                <StepCard.HeaderColumn>
                  <StepCard.Title>{step.title || DEFAULT_PROMPT_INPUT_TITLE}</StepCard.Title>
                  <StepCard.Subtitle>
                    {step.subtitle || DEFAULT_PROMPT_INPUT_SUBTITLE}
                  </StepCard.Subtitle>
                </StepCard.HeaderColumn>
              </StepCard.HeaderLeft>
            </StepCard.Header>
            <StepCard.Body>
              {instructions && (!collapsibleInstruction || expandInstructions) && (
                <ChatMarkdown
                  markdown={instructions}
                  enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                  enableSingleDollarTextMath={
                    taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                  }
                  enableHtml={taskContext.htmlMarkdownConfig?.enableInstructions}
                  enableSoftBreaks
                />
              )}
            </StepCard.Body>
          </StepCard>
        </StepWidthWrapper>
      )}
      <div className="flex flex-row">
        {response && (
          <>
            <StepWidthWrapper>
              <EditableChatMessage
                className="w-full"
                message={{
                  role: response?.context?.role || MessageRole.User,
                  content: response.output,
                }}
                defaultDisplayType={promptInputDisplayType}
                onUpdateMessage={(
                  newMessage: string,
                  role: MessageRole,
                  shouldResetHistory: boolean,
                  editedReferenceTexts: ReferenceText[],
                  editedAttachments: Attachment[],
                  skipPromptInput: boolean,
                  autoCritiqueResponse?: string,
                  autoFeedbackResponse?: AutoFeedbackChatResponse,
                ) => {
                  return onSubmit(
                    {
                      type: InteractionStepType.PromptInput,
                      context: {
                        referenceTexts: editedReferenceTexts,
                        attachments: editedAttachments,
                        autoCritiqueResponse,
                        role,
                        skipPromptInput,
                      },
                      output: newMessage,
                      autoFeedbackResponse,
                    },
                    responseIndex,
                    step,
                    shouldResetHistory,
                  );
                }}
                readOnly={readOnly}
                referenceTexts={referenceTexts}
                enablePinning={true} // TODO: make this configurable
                workerCommentProps={{
                  showCommentIcon: true,
                  childId: `${step.type}-${responseIndex}`,
                  canEdit: !!taskContext.canEditComments,
                  areCommentsReadOnly: !!taskContext.areCommentsReadOnly,
                  turnIndex: 0,
                }}
                responseTemplatingProps={{
                  showResponseTemplatingIcon: !!taskContext.responseTemplatingMode,
                  childId: `${step.type}-${responseIndex}`,
                  metadata: { stepIndex: responseIndex },
                }}
                attachments={attachments}
                enableRTLText={step.params.enable_rtl_text}
                autoCritiqueResponse={autoCritiqueResponse}
                turnIndex={0}
                responseIndex={responseIndex}
                autoFeedbackProps={{
                  childId: `${taskContext.assignmentId ?? taskContext.taskId}-${
                    InteractionStepType.PromptInput
                  }-${0}`,
                }}
              />
            </StepWidthWrapper>
            <ContributorFeedbackBox className="ml-4" step={step} />
          </>
        )}
        {!readOnly && !complete && !response && (
          <>
            <StepWidthWrapper>
              <InteractionInput
                onSubmit={(prompt, autoCritiqueResponse, autoFeedbackResponse) =>
                  onSubmit(
                    {
                      type: InteractionStepType.PromptInput,
                      context: {
                        referenceTexts,
                        attachments,
                        autoCritiqueResponse,
                      },
                      output: prompt,
                      autoFeedbackResponse,
                    },
                    responseIndex,
                    step,
                  )
                }
                placeholder={
                  step.params.enable_rtl_text ? 'Start typing here' : 'Start typing here...'
                }
                referenceTexts={referenceTexts}
                setReferenceTexts={setReferenceTexts}
                responseIndex={responseIndex}
                attachments={attachments}
                setAttachments={setAttachments}
                enableRTLText={step.params.enable_rtl_text}
                autoCritiqueResponse={autoCritiqueResponse}
                autoFeedbackProps={{
                  childId: `${taskContext.assignmentId ?? taskContext.taskId}-${
                    InteractionStepType.PromptInput
                  }-${responseIndex}`,
                }}
                isMultiTurnStep={false}
                initialPromptText={evaluatedPromptText}
                modelPromptSelector={getModelPromptSelector()}
                selectedPrompt={selectedPrompt}
                setSelectedPrompt={setSelectedPrompt}
                displaySuggestionText={displaySuggestionText}
              />
            </StepWidthWrapper>
            <ContributorFeedbackBox className="ml-4" step={step} />
          </>
        )}
      </div>
    </>
  );
};

export default memo(PromptInputTaskComponent);
